import { useEffect, useRef, useState } from 'react';
import { animate, motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import Logo from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import ParticleBackground from '../components/ParticleBackground';
import { Expandable } from '../components/Expandable';
import posterNyc from '../assets/posters/poster_climb_nyc.jpg';
import posterPottery from '../assets/posters/poster_pottery_london.jpeg';
import posterBooks from '../assets/posters/poster_bookclub_chicago.jpeg';

// hosts.outclose.com: the member-facing twin of venues.outclose.com. A member applies to be the
// Outclose co-host of a Groups night in their city. The form posts to the
// marketing server (POST /api/cohosts/apply); we never meet applicants in
// person, so the form carries everything we judge them on.
const COHOST_API = import.meta.env.VITE_API_URL ?? '';

// The active Outclose city list (2026-10-21). Keep in step with COHOST_CITIES
// in shared/types/cohosts.ts (this app does not import shared/).
const CITIES: { id: string; name: string }[] = [
  { id: 'usa_atlanta', name: 'Atlanta' }, { id: 'usa_austin', name: 'Austin' }, { id: 'de_berlin', name: 'Berlin' },
  { id: 'usa_boston', name: 'Boston' }, { id: 'be_brussels', name: 'Brussels' }, { id: 'usa_chicago', name: 'Chicago' },
  { id: 'usa_dallas', name: 'Dallas' }, { id: 'usa_denver', name: 'Denver' }, { id: 'ie_dublin', name: 'Dublin' },
  { id: 'usa_fort_lauderdale', name: 'Fort Lauderdale' }, { id: 'usa_houston', name: 'Houston' }, { id: 'uk_london', name: 'London' },
  { id: 'usa_la', name: 'Los Angeles' }, { id: 'uk_manchester', name: 'Manchester' }, { id: 'au_melbourne', name: 'Melbourne' },
  { id: 'usa_miami', name: 'Miami' }, { id: 'usa_minneapolis', name: 'Minneapolis' }, { id: 'ca_montreal', name: 'Montreal' },
  { id: 'usa_nyc', name: 'New York City' }, { id: 'fr_paris', name: 'Paris' }, { id: 'usa_philadelphia', name: 'Philadelphia' },
  { id: 'usa_phoenix', name: 'Phoenix' }, { id: 'usa_san_diego', name: 'San Diego' }, { id: 'usa_sf', name: 'San Francisco' },
  { id: 'usa_seattle', name: 'Seattle' }, { id: 'au_sydney', name: 'Sydney' }, { id: 'ca_toronto', name: 'Toronto' },
  { id: 'ca_vancouver', name: 'Vancouver' }, { id: 'usa_dc', name: 'Washington D.C.' },
];
const ACTIVITIES = ['Climbing', 'Boxing', 'Pottery', 'Life drawing', 'Pickleball or padel', 'Book club', 'Something else'];
const NIGHTS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const EASE = [0.16, 1, 0.3, 1] as const;

const VIEW = { once: true, margin: '-12% 0px -12% 0px' } as const;

// Below the fold: rise once, when the block scrolls into view.
function useRise() {
  const reduce = useReducedMotion();
  return (d = 0) =>
    reduce
      ? { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: VIEW, transition: { duration: 0.5, delay: d } }
      : { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: VIEW, transition: { duration: 0.9, ease: EASE, delay: d } };
}
// First screen: rise on mount.
function useRiseNow() {
  const reduce = useReducedMotion();
  return (d = 0) =>
    reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.5, delay: d } }
      : { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, ease: EASE, delay: d } };
}

// A figure that counts up from zero the first time it is seen.
function Counter({ to, className }: { to: number; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-10% 0px' });
  const reduce = useReducedMotion();
  useEffect(() => {
    const el = ref.current;
    if (!el || !inView) return;
    if (reduce) { el.textContent = to.toLocaleString(); return; }
    const c = animate(0, to, { duration: 1.6, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => { el.textContent = Math.round(v).toLocaleString(); } });
    return () => c.stop();
  }, [inView, to, reduce]);
  return <span ref={ref} className={className}>0</span>;
}

const WORD = {
  hidden: { opacity: 0, y: '0.6em' },
  show: (i: number) => ({ opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE, delay: 0.1 + i * 0.07 } }),
};

function Shell({ children, className = '', id }: { children: React.ReactNode; className?: string; id?: string }) {
  return (
    <div id={id} className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </div>
  );
}
function Kicker({ children }: { children: React.ReactNode }) {
  return <span className="inline-block text-sm font-semibold text-primary-500 dark:text-primary-400">{children}</span>;
}
const h2Class = 'mt-5 font-Poppins text-[clamp(1.85rem,4.4vw,3rem)] font-bold leading-[1.14] tracking-[-0.02em] text-[#1c1510] [text-wrap:balance] dark:text-[#f3ece4]';
const bodyClass = 'text-[#473d34] dark:text-[#dccfc0] leading-[1.7] tracking-[0.005em]';

function Header() {
  return (
    <header>
      <Shell className="flex items-center justify-between py-6">
        <Logo />
        <div className="flex items-center gap-4">
          <ThemeToggle />
          <a href="#apply" className="btn-primary text-sm">Apply to co-host</a>
        </div>
      </Shell>
    </header>
  );
}

function Hero() {
  const rise = useRiseNow();
  const reduce = useReducedMotion();
  return (
    <section className="relative">
      <Shell className="grid items-center gap-12 pb-16 pt-10 sm:pt-16 lg:grid-cols-[1.1fr_0.9fr] lg:pb-24">
        <div>
          <motion.div {...rise(0)}><Kicker>Co-host an Outclose Group</Kicker></motion.div>
          <motion.h1
            {...rise(0.08)}
            className="mt-7 font-Poppins font-bold tracking-[-0.025em] text-[#1c1510] dark:text-[#f3ece4] [text-wrap:balance] text-[clamp(3rem,7vw,6.5rem)] leading-[0.98]"
          >
            {['Be', 'the', 'reason', 'strangers'].map((w, i) => (
              <span key={w}>
                <span className="inline-block overflow-hidden pb-[0.08em] -mb-[0.08em] align-bottom">
                  <motion.span className="inline-block" variants={WORD} initial={reduce ? 'show' : 'hidden'} animate="show" custom={i}>{w}</motion.span>
                </span>{' '}
              </span>
            ))}
            <span className="relative inline-block whitespace-nowrap text-primary-500 overflow-visible">
              <span className="inline-block overflow-hidden pb-[0.08em] -mb-[0.08em] align-bottom">
                <motion.span className="inline-block" variants={WORD} initial={reduce ? 'show' : 'hidden'} animate="show" custom={4}>feel at home.</motion.span>
              </span>
              <svg aria-hidden viewBox="0 0 200 14" preserveAspectRatio="none" className="absolute -bottom-2 left-0 h-3 w-full text-primary-500/60">
                <motion.path
                  d="M2 9 C 50 3, 150 3, 198 8" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"
                  initial={reduce ? { pathLength: 1 } : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
                />
              </svg>
            </span>
          </motion.h1>
          <motion.p {...rise(0.16)} className={`mt-8 max-w-[52ch] text-lg sm:text-xl ${bodyClass}`}>
            An Outclose Group is one activity, one weeknight a month, the same LGBTQ+ faces every time. The co-host is the person at the door who knows your name. We'd like that to be you.
          </motion.p>
          <motion.div {...rise(0.24)} className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#apply" className="btn-primary group inline-flex items-center gap-2 px-8 py-3 text-base">
              Apply to co-host
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </a>
            <a href="#what" className="font-Poppins font-semibold text-[#1c1510] dark:text-[#f3ece4] hover:text-primary-500">What it takes</a>
          </motion.div>
        </div>

        {/* Poster fan */}
        <div className="relative mx-auto h-[340px] w-full max-w-md sm:h-[420px]">
          {[
            { src: posterPottery, cls: 'left-0 top-10', rot: -6, z: 'z-10', delay: 0.25, float: 5 },
            { src: posterNyc, cls: 'left-1/2 top-0 -translate-x-1/2', rot: 2, z: 'z-20', delay: 0.4, float: 7 },
            { src: posterBooks, cls: 'right-0 top-12', rot: 6, z: 'z-10', delay: 0.55, float: 6 },
          ].map((p) => (
            <motion.div
              key={p.src}
              className={`absolute ${p.cls} ${p.z} w-[58%]`}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 80, rotate: p.rot + 10, scale: 0.92 }}
              animate={{ opacity: 1, y: 0, rotate: p.rot, scale: 1 }}
              transition={{ duration: 1.1, ease: EASE, delay: p.delay }}
              whileHover={reduce ? undefined : { y: -10, scale: 1.04, rotate: p.rot / 2, zIndex: 30, transition: { duration: 0.35, ease: EASE } }}
            >
              <motion.img
                src={p.src}
                alt=""
                className="w-full rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/10"
                animate={reduce ? undefined : { y: [0, -p.float, 0] }}
                transition={{ duration: 5 + p.float * 0.4, ease: 'easeInOut', repeat: Infinity, delay: p.delay + 1 }}
              />
            </motion.div>
          ))}
        </div>
      </Shell>
    </section>
  );
}

function Stats() {
  const rise = useRise();
  const stats: [React.ReactNode, string][] = [
    [<Counter to={70000} />, 'members'],
    [<Counter to={30} />, 'cities'],
    [<Counter to={8} />, 'countries'],
  ];
  return (
    <section className="border-y border-black/[0.07] dark:border-white/[0.07] bg-black/[0.02] dark:bg-white/[0.03]">
      <Shell className="grid grid-cols-3 gap-8 py-10 text-center">
        {stats.map(([n, l], i) => (
          <motion.div key={l} {...rise(0.05 * i)}>
            <p className="font-Poppins text-3xl font-bold tracking-[-0.02em] text-[#1c1510] dark:text-[#f3ece4] sm:text-4xl">{n}</p>
            <p className={`mt-1 text-sm ${bodyClass}`}>{l}</p>
          </motion.div>
        ))}
      </Shell>
    </section>
  );
}

function Dream() {
  const rise = useRise();
  const beats = [
    ['A room that knows you', 'Most people on a Group night came alone. You’re the one they recognise the second time, and the reason there is a second time.'],
    ['Your city, closer', 'Queer community in a city is mostly built by a few people who keep showing up. This is a way to be one of them, with the boring parts already done.'],
    ['One night a month that’s yours', 'Same activity, same night, a group that becomes a group. You climb, throw, spar or read with them. You just get there first.'],
  ];
  return (
    <section>
      <Shell className="py-20 sm:py-28">
        <motion.div {...rise(0)}>
          <Kicker>Why do it</Kicker>
          <h2 className={h2Class}>Someone has to be the person who says hi first.</h2>
        </motion.div>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {beats.map(([h, p], i) => (
            <motion.div
              key={h}
              {...rise(0.08 * i)}
              whileHover={{ y: -6, transition: { duration: 0.3, ease: EASE } }}
              className="rounded-3xl border border-black/[0.07] bg-white/60 p-8 transition-colors duration-300 hover:border-primary-500/40 dark:border-white/[0.07] dark:bg-white/[0.04]"
            >
              <p className="font-Poppins text-2xl font-bold tracking-[-0.01em] text-[#1c1510] dark:text-[#f3ece4]">{h}</p>
              <p className={`mt-3 ${bodyClass}`}>{p}</p>
            </motion.div>
          ))}
        </div>
      </Shell>
    </section>
  );
}

function Expected() {
  const rise = useRise();
  const steps = [
    ['Be there', 'Make 3 of every 4 of your Group’s events. Arrive 15 minutes early and greet people as they come in, especially anyone who came alone.'],
    ['Keep the chat alive', 'Answer questions between events and get people looking forward to the next one. A few messages a week is plenty.'],
    ['Take a few photos', 'A handful of photos or a short clip each night, for the Group chat and for us. Members agree to photos when they join, and anyone who’d rather not be in them just tells you.'],
    ['Tell us what you saw', 'Two lines after each night on what worked and what didn’t. If you can’t make one, tell us a week ahead so we can cover it.'],
  ];
  return (
    <section id="what" className="bg-primary-500 text-white">
      <Shell className="py-20 sm:py-24">
        <motion.div {...rise(0)}>
          <span className="inline-block text-sm font-semibold text-white/80">What we ask</span>
          <h2 className="mt-5 font-Poppins text-[clamp(1.85rem,4.4vw,3rem)] font-bold leading-[1.14] tracking-[-0.02em] [text-wrap:balance]">
            Four things. We do everything else.
          </h2>
          <p className="mt-6 max-w-[52ch] text-white/85 leading-[1.7]">
            Venues, bookings, payments, reminders and scheduling are on us. An Outclose community manager runs the calendar and nudges the chat, so you are never the only one talking.
          </p>
        </motion.div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map(([h, p], i) => (
            <motion.div
              key={h}
              {...rise(0.08 * i)}
              whileHover={{ y: -6, transition: { duration: 0.3, ease: EASE } }}
              className="rounded-3xl bg-white/10 p-6 ring-1 ring-white/20 backdrop-blur"
            >
              <p className="font-Poppins text-5xl font-bold tabular-nums text-white/60">{i + 1}</p>
              <p className="mt-4 font-Poppins text-xl font-semibold">{h}</p>
              <p className="mt-2 text-white/85 leading-[1.7]">{p}</p>
            </motion.div>
          ))}
        </div>
      </Shell>
    </section>
  );
}

function GiveGet() {
  const rise = useRise();
  const get = ['Every event you host is free for you. You never pay for the climbing session, the pottery class or the court.', 'A free year of Outclose', 'A co-host badge on your profile', 'First say on where and when the Group meets', 'A direct line to the Outclose team'];
  const need = ['Most of your Group’s nights, 15 minutes early', 'A few messages a week in the Group chat', 'A few photos from each night', 'Two lines of feedback after each night', 'A week’s notice when you can’t make one'];
  return (
    <section>
      <Shell className="pt-20 pb-4 sm:pt-28 sm:pb-6">
        <div className="grid gap-6 lg:grid-cols-2">
          {[['What you get', get, 'text-emerald-500'], ['What we need', need, 'text-primary-500']].map(([title, items, color], i) => (
            <motion.div key={title as string} {...rise(0.08 * i)} className="rounded-3xl border border-black/[0.07] bg-white/60 p-8 dark:border-white/[0.07] dark:bg-white/[0.04]">
              <h3 className="font-Poppins text-2xl font-bold tracking-[-0.01em] text-[#1c1510] dark:text-[#f3ece4]">{title as string}</h3>
              <ul className="mt-5 space-y-3">
                {(items as string[]).map((t, j) => (
                  <motion.li
                    key={t}
                    className={`flex gap-3 ${bodyClass}`}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={VIEW}
                    transition={{ duration: 0.6, ease: EASE, delay: 0.25 + 0.08 * i + 0.07 * j }}
                  >
                    <motion.span initial={{ scale: 0.4, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={VIEW} transition={{ duration: 0.5, ease: [0.34, 1.45, 0.64, 1], delay: 0.35 + 0.08 * i + 0.07 * j }}>
                      <Check className={`mt-1 h-5 w-5 shrink-0 ${color as string}`} />
                    </motion.span>
                    {t}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <motion.div {...rise(0.2)} className="mt-12">
          <Kicker>Groups starting now</Kicker>
          <div className="mt-4 flex flex-wrap gap-2">
            {['Climbing', 'Boxing', 'Pottery', 'Life drawing', 'Pickleball or padel', 'Book club'].map((a, j) => (
              <motion.span
                key={a}
                initial={{ opacity: 0, scale: 0.85, y: 8 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={VIEW}
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                transition={{ duration: 0.5, ease: [0.34, 1.45, 0.64, 1], delay: 0.1 + j * 0.06 }}
                className="rounded-full border border-black/10 px-4 py-2 font-Poppins text-sm font-semibold text-[#1c1510] transition-colors hover:border-primary-500 hover:text-primary-500 dark:border-white/15 dark:text-[#f3ece4]"
              >{a}</motion.span>
            ))}
          </div>
        </motion.div>
      </Shell>
    </section>
  );
}

const FAQ: { q: string; a: string }[] = [
  { q: 'What is an Outclose Group?', a: 'A recurring activity night for LGBTQ+ members in one city: the same climbing gym, pottery studio, boxing gym, pickleball court or book club, on the same weeknight, with the same people. Outclose brings the group, books the venue and takes the payments. The co-host makes it feel like a group.' },
  { q: 'Do I run the activity?', a: 'No. The venue’s staff run the session as they always do. You are a member who gets there first, says hi, and keeps everyone together between the door and the activity.' },
  { q: 'Is this a job?', a: 'No, and there is no pay. It is a volunteer role with real perks: the event you host is free for you, Outclose is free for a year, and you have first say on where and when the Group meets.' },
  { q: 'How much time is it?', a: 'One night a month at the venue, 15 minutes early, plus a few messages a week in the Group chat and two lines of feedback after each night. If the Group grows to two nights a month, we ask you first.' },
  { q: 'What if I can’t make one?', a: 'Tell us a week ahead and we cover it, either with another co-host or a community manager. We ask for 3 of every 4 nights, so one miss now and then is fine.' },
  { q: 'What about the photos?', a: 'A handful of phone photos or a short clip each night. Members agree to photos at Group events when they join, and we use them on outclose.com and in the app. Anyone who would rather not be in them tells you at the start and you leave them out.' },
  { q: 'Can I co-host more than one Group?', a: 'Start with one. If it runs well for a few months and you want a second, ask us.' },
  { q: 'Who picks the venue and the night?', a: 'You get first say. We take your preference to the venues we have in your city and book the one that fits. If none fit yet, we go and find one.' },
  { q: 'How do you choose co-hosts?', a: 'From the form. We read your answers, look at your Instagram and anything else you link, and reply by email. We do not do calls, so say what you would have said on one.' },
  { q: 'Can I stop?', a: 'Yes. Give us a month so we can find the next co-host, and the Group keeps going.' },
];

function Faq() {
  const rise = useRise();
  return (
    <Shell className="py-20 sm:py-28">
      <motion.div {...rise(0)}>
        <Kicker>FAQ</Kicker>
        <h2 className={h2Class}>Questions people ask before they apply.</h2>
      </motion.div>
      <motion.div {...rise(0.1)} className="mt-12 flex flex-col gap-4">
        {FAQ.map((f) => (
          <Expandable key={f.q} title={f.q}>{f.a}</Expandable>
        ))}
      </motion.div>
    </Shell>
  );
}

function Field({ label, name, placeholder, type = 'text', required, maxLength }: { label: string; name: string; placeholder: string; type?: string; required?: boolean; maxLength?: number }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      <input name={name} type={type} placeholder={placeholder} required={required} maxLength={maxLength} className="input h-12" />
    </div>
  );
}

// A row of checkbox chips. "At least one" is checked on submit, not per box.
function Chips({ name, options }: { name: string; options: string[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <label key={o} className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-neutral-300 px-3 py-2 text-sm transition-colors has-[:checked]:border-primary-500 has-[:checked]:bg-primary-500/10 dark:border-neutral-600">
          <input type="checkbox" name={name} value={o} className="h-4 w-4 accent-primary-500" /> {o}
        </label>
      ))}
    </div>
  );
}

const WHY_MAX = 600;

function Apply() {
  const rise = useRise();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hosted, setHosted] = useState<'yes' | 'no' | ''>('');
  const [why, setWhy] = useState('');

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const text = (k: string) => String(f.get(k) ?? '').trim() || null;
    if (f.getAll('activities').length === 0) { setError('Pick at least one activity you would host.'); return; }
    if (!hosted) { setError('Tell us whether you have hosted anything before.'); return; }
    if (f.getAll('nights').length === 0) { setError('Pick at least one night you are usually free.'); return; }
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${COHOST_API}/api/cohosts/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: text('name'), email: text('email'), phone: text('phone'), cityId: text('cityId'),
          activities: f.getAll('activities').map(String), instagram: text('instagram'), otherSocials: text('otherSocials'),
          yearsInCity: text('yearsInCity'), hostedBefore: hosted === 'yes', hostedWhat: hosted === 'yes' ? text('hostedWhat') : null,
          why: text('why'), nights: f.getAll('nights').map(String), commitment: f.get('commitment') === 'on',
          company: String(f.get('company') ?? ''),
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) throw new Error(json?.error ?? 'Something went wrong. Email team@outclose.com and we will sort it.');
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="apply" className="border-t border-black/[0.07] dark:border-white/[0.07]">
      <Shell className="grid gap-12 py-20 sm:py-28 lg:grid-cols-[0.8fr_1.2fr]">
        <motion.div {...rise(0)}>
          <Kicker>Five minutes</Kicker>
          <h2 className={h2Class}>Apply to co-host.</h2>
          <p className={`mt-6 max-w-[40ch] ${bodyClass}`}>We don't do interview calls, so this form is the interview. Say what you'd have said on one. We read every application and reply by email within a week.</p>
          <p className={`mt-6 text-sm ${bodyClass}`}>Questions? team@outclose.com.</p>
        </motion.div>
        <motion.div {...rise(0.1)} className="rounded-3xl border border-black/[0.07] bg-white/70 p-6 dark:border-white/[0.07] dark:bg-white/[0.04] sm:p-8">
          {sent ? (
            <div className="py-10 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"><Check className="h-7 w-7" /></div>
              <p className="font-Poppins text-2xl font-bold text-[#1c1510] dark:text-[#f3ece4]">Got it.</p>
              <p className={`mt-2 ${bodyClass}`}>We'll reply by email within a week. If it's a yes, your membership is on us from your first night as co-host.</p>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={submit}>
              {/* Honeypot: people never see it, bots fill it. */}
              <input name="company" type="text" tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Your name" name="name" placeholder="Sam" required maxLength={120} />
                <Field label="Email" name="email" placeholder="sam@email.com" type="email" required maxLength={200} />
                <Field label="Phone (optional)" name="phone" placeholder="+1" maxLength={60} />
                <div>
                  <label className="block text-sm font-medium mb-1.5">City</label>
                  <select name="cityId" className="input h-12" defaultValue="" required>
                    <option value="" disabled>Pick one</option>
                    {CITIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">What would you host?</label>
                <Chips name="activities" options={ACTIVITIES} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Instagram" name="instagram" placeholder="@yourhandle" required maxLength={120} />
                <Field label="Other socials (optional)" name="otherSocials" placeholder="TikTok, LinkedIn, a link" maxLength={500} />
              </div>
              <Field label="How long have you lived in the city?" name="yearsInCity" placeholder="4 years, or grew up here" required maxLength={60} />
              <fieldset>
                <legend className="block text-sm font-medium mb-1.5">Have you hosted or organised anything before?</legend>
                <div className="flex gap-2">
                  {(['yes', 'no'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setHosted(v)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${hosted === v ? 'border-primary-600 bg-primary-500 text-white' : 'border-neutral-300 bg-white dark:border-neutral-600 dark:bg-neutral-800'}`}
                    >
                      {v === 'yes' ? 'Yes' : 'Not yet'}
                    </button>
                  ))}
                </div>
              </fieldset>
              {hosted === 'yes' && (
                <div>
                  <label className="block text-sm font-medium mb-1.5">What did you run, and for how long?</label>
                  <textarea name="hostedWhat" rows={3} maxLength={1000} required placeholder="A run club, a game night at your place, a Meetup group, a team at work. Anything counts." className="input w-full py-3" />
                </div>
              )}
              <div>
                <div className="flex items-baseline justify-between">
                  <label className="block text-sm font-medium mb-1.5">Why you?</label>
                  <span className={`text-xs tabular-nums ${why.length > WHY_MAX - 50 ? 'text-primary-500' : 'text-neutral-400'}`}>{why.length}/{WHY_MAX}</span>
                </div>
                <textarea name="why" rows={4} maxLength={WHY_MAX} required value={why} onChange={(e) => setWhy(e.target.value)} placeholder="The honest version. What you'd bring to a room of people who don't know each other yet." className="input w-full py-3" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Nights you're usually free</label>
                <Chips name="nights" options={NIGHTS} />
              </div>
              <label className="flex cursor-pointer items-start gap-3 text-sm">
                <input type="checkbox" name="commitment" required className="mt-0.5 h-4 w-4 shrink-0 accent-primary-500" />
                <span className={bodyClass}>I can make 3 of every 4 of my Group's nights, arrive 15 minutes early, keep the chat going between events, take a few photos, send two lines of feedback after each night, and give a week's notice when I can't make one.</span>
              </label>
              <button type="submit" disabled={sending} className="btn-primary group inline-flex w-full items-center justify-center gap-2 py-3 text-base disabled:opacity-60">
                {sending ? 'Sending' : 'Send my application'} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
              {error && <p className="text-center text-sm text-red-600 dark:text-red-400">{error}</p>}
            </form>
          )}
        </motion.div>
      </Shell>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-black/[0.07] dark:border-white/[0.07]">
      <Shell className="flex flex-col items-start justify-between gap-6 py-12 sm:flex-row sm:items-center">
        <Logo />
        <div className="flex flex-wrap gap-7 text-sm text-[#544a40] dark:text-[#c3b5a6]">
          <a href="https://outclose.com" className="hover:text-primary-500">Outclose</a>
          <a href="https://venues.outclose.com" className="hover:text-primary-500">For venues</a>
          <a href="https://outclose.com/terms" className="hover:text-primary-500">Terms</a>
          <a href="https://outclose.com/privacy" className="hover:text-primary-500">Privacy</a>
          <a href="mailto:team@outclose.com" className="hover:text-primary-500">Contact</a>
        </div>
      </Shell>
    </footer>
  );
}

export default function Hosts() {
  return (
    <div className="relative min-h-dvh overflow-x-hidden">
      <ParticleBackground className="absolute inset-0" />
      <Header />
      <main>
        <Hero />
        <Stats />
        <Dream />
        <Expected />
        <GiveGet />
        <Faq />
        <Apply />
      </main>
      <Footer />
    </div>
  );
}
